/**
 * Rave mode (server/src/rave.ts): about thirty seconds of disco over the whole
 * window. The lights go down and a disco ball drops in, there's a countdown,
 * then the drop: the page shakes on every beat, coloured light strobes and
 * roams, lasers sweep, confetti and emoji pour down, words slam into the middle
 * of the screen and everyone at the party dances along the bottom around the
 * DJ, with a beat to match (ravebeat.ts). A breakdown, a second drop, then the
 * lights come back up.
 *
 * Nobody it's sent to can leave early: Esc just gets a "nice try", and a
 * reload picks it up again where it was. Only the person who started it can
 * stop it on their own screen. Flashes stay on the beat (about twice a second,
 * under the three-a-second limit for flashing), and with reduced motion
 * nothing shakes, strobes or sweeps.
 */
import { initials, userColor } from "../lib/app";
import { avatarUrl } from "../lib/avatars";
import { rainConfetti } from "./confetti";
import { BEAT, PLAN, playRaveBeat, type RaveBeat } from "./ravebeat";
import "./rave.css";

export interface Rave {
  id: string;
  /** Who started it: they're the DJ. */
  from: { id: number; name: string };
  sound: boolean;
  /** Everyone it went to, dancing along the bottom. */
  guests: Array<{ id: number; name: string }>;
  /** On the screen of the person starting it, before it goes to anyone. */
  preview?: boolean;
}

type Phase = "build" | "drop" | "break" | "finale" | "out";

const DANCERS = ["🕺", "💃", "🥳", "👯", "🙌", "🦄", "🤖", "👽", "🐸", "🦖", "🧑‍🎤", "🐧", "🦩", "👾"];
const FALLING = ["🎉", "✨", "🪩", "💥", "🔥", "🎶", "⭐", "🍾", "💜", "⚡", "🎊"];
const LIGHTS = ["#ff3df2", "#39e6ff", "#fff36b", "#7dff4d", "#9b5cff"];
const HATS = ["#ff3df2", "#39e6ff", "#fff36b", "#7dff4d", "#ff8a3d"];

/** What slams into the middle of the screen, by beat. */
function shouts(host: string): Record<number, [string, number]> {
  const dj = host.toUpperCase();
  return {
    1: ["GET READY 🪩", 4],
    5: ["3", 1],
    6: ["2", 1],
    7: ["1", 1],
    8: ["DROP!!", 2],
    10: [`${dj} IN THE HOUSE`, 4],
    14: ["\\begin{rave}", 4],
    18: ["ZERO ERRORS", 4],
    22: ["ONLY BANGERS", 4],
    26: ["\\usepackage{disco}", 4],
    30: ["OVERFULL \\hbox\nOF VIBES", 4],
    34: ["COMPILE THE PARTY", 4],
    38: ["LOUDER!!", 2],
    40: ["MAKE SOME NOISE 🙌", 4],
    44: ["ONE MORE TIME", 2],
    46: ["HANDS UP", 2],
    48: ["\\section*{PARTY}", 2],
    50: ["NO DEADLINES", 2],
    52: ["📯📯📯", 2],
    54: ["BIBTEX THIS", 2],
    56: ["final_FINAL_v2.tex", 2],
    58: ["LAST ONE!!", 2],
    60: ["\\end{rave}\nback to work 📚", 4],
  };
}

const PLAYED = "benleaf-rave-played";
/** The rave on in this tab, so a reload carries on with it. */
const NOW = "benleaf-rave-now";
const seen = new Set<string>();
let running = false;

const pick = <T>(list: T[]) => list[Math.floor(Math.random() * list.length)];

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, parent?: HTMLElement): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = className;
  parent?.append(e);
  return e;
}

/** Someone's face for the crowd: their picture, or their initials on their colour. */
function face(person: { id: number; name: string }, parent: HTMLElement) {
  const f = el("span", "raver-face", parent);
  f.style.background = userColor(person.id);
  f.textContent = initials(person.name);
  const src = avatarUrl(person.id);
  if (src) {
    const img = el("img", "", f);
    img.alt = "";
    img.src = src;
    img.onerror = () => img.remove();
  }
  return f;
}

/** "Alex", "Alex and Sam", "Alex, Sam and 2 others". */
function names(people: Array<{ name: string }>) {
  const n = people.map((p) => p.name);
  if (n.length <= 1) return n[0] ?? "nobody";
  if (n.length <= 3) return `${n.slice(0, -1).join(", ")} and ${n[n.length - 1]}`;
  return `${n.slice(0, 2).join(", ")} and ${n.length - 2} others`;
}

/**
 * A rave someone started, as the server sends it. A tab nobody's looking at
 * keeps it for when they come back (for ten minutes), unless another of their
 * tabs has shown it by then.
 */
export function receiveRave(event: Record<string, unknown>, me: number) {
  const rave = event as unknown as Rave;
  if (seen.has(rave.id)) return;
  seen.add(rave.id);
  if (document.visibilityState === "visible") return startRave(rave, me);
  const until = Date.now() + 10 * 60_000;
  const back = () => {
    if (document.visibilityState !== "visible") return;
    document.removeEventListener("visibilitychange", back);
    let elsewhere = false;
    try {
      elsewhere = localStorage.getItem(PLAYED) === rave.id;
    } catch {}
    if (Date.now() < until && !elsewhere) startRave(rave, me);
  };
  document.addEventListener("visibilitychange", back);
}

/** After a reload in the middle of a rave: it carries on from where it was. */
export function resumeRave(me: number) {
  let saved: { rave: Rave; me: number; startedAt: number } | null = null;
  try {
    saved = JSON.parse(sessionStorage.getItem(NOW) ?? "null");
  } catch {}
  if (!saved || saved.me !== me) return;
  if (Date.now() < saved.startedAt + PLAN.end * BEAT * 1000) startRave(saved.rave, me, saved.startedAt);
  else forget();
}

function forget() {
  try {
    sessionStorage.removeItem(NOW);
  } catch {}
}

/**
 * Start the party on this screen. `me` is who's watching, so the DJ's own
 * screen says so. `startedAt` is when beat 0 was (Date.now() time), to pick
 * one up again after a reload.
 */
export function startRave(rave: Rave, me: number, startedAt = Date.now() + 60) {
  if (running) return;
  running = true;
  seen.add(rave.id);
  try {
    localStorage.setItem(PLAYED, rave.id);
  } catch {}

  const html = document.documentElement;
  const root = document.getElementById("root");
  const calm = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const mine = rave.from.id === me;
  // Everyone it was sent to is staying until the end; whoever started it can stop it.
  const trapped = !rave.preview && !mine;
  const resumed = startedAt < Date.now();
  html.style.setProperty("--beat", `${BEAT}s`);
  // Shake around the middle of what's on screen, even on a scrolled page.
  if (root) root.style.transformOrigin = `50% ${scrollY + innerHeight / 2}px`;

  // ------------------------------------------------ the room

  const dark = el("div", "rave-dark");
  const lights = el("div", "rave-lights");
  LIGHTS.forEach((c, i) => {
    const spot = el("span", "rave-spot", lights);
    spot.style.setProperty("--c", c);
    spot.style.animationDuration = `${4 + i * 1.3}s`;
    spot.style.animationDelay = `${-i * 1.7}s`;
    if (i % 2) spot.style.animationDirection = "alternate-reverse";
  });
  const specks = el("div", "rave-specks", lights);
  for (let i = 0; i < 80; i++) {
    const s = el("span", "rave-speck", specks);
    const angle = Math.random() * Math.PI * 2;
    const r = 6 + Math.random() * 90;
    s.style.transform = `translate(${Math.cos(angle) * r}vmax, ${Math.sin(angle) * r}vmax)`;
    s.style.setProperty("--s", `${3 + Math.random() * 6}px`);
    s.style.animationDelay = `${-Math.random() * 1.4}s`;
  }
  el("div", "rave-strobe", lights);

  const stage = el("div", "rave-stage");
  stage.setAttribute("aria-hidden", "true");
  const lasers = el("div", "rave-lasers", stage);
  const beam = (side: string, angle: number, i: number) => {
    const l = el("span", `rave-laser from-${side}`, lasers);
    l.style.setProperty("--a", `${angle}deg`);
    l.style.setProperty("--c", LIGHTS[i % 4]);
    l.style.animationDuration = `${BEAT * (side === "top" ? 4 : 2)}s`;
    l.style.animationDelay = `${-BEAT * (i % 3) * 0.66}s`;
  };
  for (let i = 0; i < 5; i++) {
    beam("left", 20 + i * 12, i);
    beam("right", -20 - i * 12, i + 1);
  }
  for (let i = 0; i < 7; i++) beam("top", -60 + i * 20, i + 2);

  const ballWrap = el("div", "rave-ball-wrap", stage);
  el("span", "rave-string", ballWrap);
  el("span", "rave-ball", ballWrap);

  const eq = el("div", "rave-eq", stage);
  const bars = Array.from({ length: 32 }, () => el("span", "", eq));

  // The DJ in the middle, the guests either side of them, and whoever else turned up to fill the floor.
  const crowd = el("div", "rave-crowd", stage);
  const guests = rave.guests.filter((g) => g.id !== rave.from.id);
  const slots = Math.max(2, Math.min(16, Math.floor((innerWidth - 240) / 76)));
  const dancers: Array<{ id: number; name: string } | string> = [...guests.slice(0, slots)];
  while (dancers.length < slots) dancers.push(pick(DANCERS));
  // Guests nearest the DJ, alternating sides.
  const left: typeof dancers = [];
  const right: typeof dancers = [];
  dancers.forEach((d, i) => (i % 2 ? right : left).push(d));
  const raver = (d: (typeof dancers)[number], i: number) => {
    const r = el("div", "raver", crowd);
    r.style.setProperty("--h", `${18 + Math.random() * 26}px`);
    r.style.setProperty("--tilt", `${(Math.random() - 0.5) * 16}deg`);
    r.style.animationDelay = i % 2 ? `${-BEAT / 2}s` : "0s";
    if (typeof d === "string") {
      el("span", "raver-emoji", r).textContent = d;
      return;
    }
    el("span", "raver-hat", r).style.setProperty("--hat", HATS[i % HATS.length]);
    face(d, r).style.setProperty("--c", userColor(d.id));
    el("span", "raver-name", r).textContent = d.name;
  };
  let n = 0;
  for (const d of left.reverse()) raver(d, n++);
  const dj = el("div", "rave-dj", crowd);
  const head = el("div", "rave-dj-head", dj);
  face(rave.from, head);
  el("span", "rave-headphones", head).textContent = "🎧";
  const decks = el("div", "rave-decks", dj);
  el("span", "rave-vinyl", decks);
  el("span", "rave-dj-name", decks).textContent = `DJ ${rave.from.name.toUpperCase()}`;
  el("span", "rave-vinyl", decks);
  for (const d of right) raver(d, n++);

  const falling = el("div", "rave-falling", stage);
  const shout = el("div", "rave-shout", stage);

  const banner = el("div", "rave-banner");
  banner.setAttribute("role", "status");
  const text = el("span", "rave-banner-text", banner);
  const say = () => {
    text.replaceChildren("🪩 ");
    if (rave.preview) text.append("Rave preview: only you can see this");
    else if (mine) text.append(`Your rave is on for ${names(guests)}`);
    else text.append(Object.assign(document.createElement("strong"), { textContent: rave.from.name }), " started a rave");
  };
  say();
  const soundBtn = el("button", "rave-sound", banner);
  soundBtn.type = "button";
  soundBtn.textContent = "🔊 Sound on";
  soundBtn.hidden = true;
  if (!trapped) {
    const stop = el("button", "rave-leave", banner);
    stop.type = "button";
    stop.title = "Esc";
    stop.textContent = "Stop";
    stop.onclick = () => finish();
  }

  document.body.append(dark, lights, stage, banner);
  // Laid out before the phase is set, so the lights fade down rather than snapping. Back from a reload, they're already down.
  if (resumed) for (const l of [dark, lights]) l.style.transitionDuration = "0.3s";
  void dark.offsetWidth;

  // ------------------------------------------------ the night

  const beat0 = performance.now() + (startedAt - Date.now());
  if (trapped)
    try {
      sessionStorage.setItem(NOW, JSON.stringify({ rave, me, startedAt }));
    } catch {}
  let beat: RaveBeat | null = null;
  let done = false;
  let release = () => {};
  if (rave.sound && typeof AudioContext !== "undefined") {
    // Several tabs on screen at once all dance, but only one plays the music.
    const play = () => (beat = playRaveBeat(beat0));
    if (navigator.locks)
      navigator.locks
        .request(`benleaf-rave-${rave.id}`, { ifAvailable: true }, (lock) => {
          if (!lock || done) return;
          play();
          return new Promise<void>((resolve) => (release = resolve));
        })
        .catch(() => {});
    else play();
  }
  // Browsers hold sound back until the page has been clicked or typed in.
  const checkSound = window.setTimeout(() => {
    if (beat?.blocked()) soundBtn.hidden = false;
  }, 400);
  soundBtn.onclick = () => {
    beat?.unblock().then((ok) => (soundBtn.hidden = ok));
  };

  const lines = shouts(rave.from.name);
  function slam(words: string, beats: number) {
    shout.textContent = words;
    shout.style.setProperty("--len", String(beats));
    shout.classList.remove("on");
    void shout.offsetWidth;
    shout.classList.add("on");
  }

  function pour(count: number) {
    if (calm) return;
    for (let i = 0; i < count; i++) {
      const e = el("span", "rave-emoji", falling);
      e.textContent = pick(FALLING);
      e.style.left = `${Math.random() * 100}%`;
      e.style.fontSize = `${22 + Math.random() * 30}px`;
      e.style.setProperty("--spin", `${(Math.random() - 0.5) * 900}deg`);
      e.style.animationDuration = `${1.6 + Math.random() * 1.6}s`;
      e.addEventListener("animationend", () => e.remove());
    }
  }

  const phase = (p: Phase) => (html.dataset.rave = p);

  /** Beat `b` has arrived. `late` beats were missed (a background tab), so only the phase changes. */
  function onBeat(b: number, late: boolean) {
    if (b === PLAN.build) phase("build");
    else if (b === PLAN.drop) phase("drop");
    else if (b === PLAN.breakdown) phase("break");
    else if (b === PLAN.drop2) phase("finale");
    else if (b === PLAN.out) phase("out");
    else if (b >= PLAN.end) return finish();
    if (late) return;
    const line = lines[b];
    if (line) slam(...line);
    if (b === PLAN.drop) rainConfetti(320);
    if (b === PLAN.drop2) rainConfetti(420);
    if (b > PLAN.drop2 && (b - PLAN.drop2) % 4 === 0 && b < PLAN.out) rainConfetti(70);
    if ((b >= PLAN.drop && b < PLAN.breakdown) || (b >= PLAN.drop2 && b < PLAN.out)) pour(b >= PLAN.drop2 ? 5 : 2);
  }

  /** The equaliser: the music's own levels, or a made-up bounce on the beat when there's no sound. */
  function meter(beats: number) {
    const levels = beat?.levels();
    const loud = html.dataset.rave === "drop" || html.dataset.rave === "finale";
    const kick = Math.max(0, 1 - (beats % 1) * 1.7);
    bars.forEach((bar, i) => {
      const v = levels
        ? levels[Math.floor((i / bars.length) ** 1.6 * 40)] / 255
        : (loud ? 0.25 + 0.75 * kick : 0.15) * (0.55 + 0.45 * Math.sin(i * 1.9 + beats * 2.6));
      bar.style.transform = `scaleY(${(0.04 + 0.96 * v).toFixed(3)})`;
    });
  }

  let shown = -1;
  let raf = 0;
  function frame(now: number) {
    const beats = (now - beat0) / 1000 / BEAT;
    const b = Math.floor(beats);
    while (shown < b && !done) {
      shown++;
      onBeat(shown, shown < b);
    }
    if (done) return;
    meter(Math.max(0, beats));
    raf = requestAnimationFrame(frame);
  }
  raf = requestAnimationFrame(frame);
  // Animation frames stop in a background tab; the party still has to end.
  const backstop = window.setTimeout(() => finish(), beat0 + PLAN.end * BEAT * 1000 - performance.now() + 2000);

  let unsay = 0;
  function nope() {
    text.replaceChildren("🔒 Nice try. Nobody leaves this party.");
    banner.classList.remove("nope");
    void banner.offsetWidth;
    banner.classList.add("nope");
    clearTimeout(unsay);
    unsay = window.setTimeout(say, 2000);
  }

  // Caught on the way down, before the editor (which has its own uses for Esc) can keep it.
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== "Escape") return;
    if (trapped) nope();
    else finish();
  };
  window.addEventListener("keydown", onKey, true);

  function finish() {
    if (done) return;
    done = true;
    cancelAnimationFrame(raf);
    clearTimeout(backstop);
    clearTimeout(checkSound);
    clearTimeout(unsay);
    window.removeEventListener("keydown", onKey, true);
    forget();
    beat?.stop();
    release();
    delete html.dataset.rave;
    const layers = [dark, lights, stage, banner];
    for (const l of layers) l.classList.add("rave-gone");
    window.setTimeout(() => {
      for (const l of layers) l.remove();
      html.style.removeProperty("--beat");
      if (root) root.style.transformOrigin = "";
      running = false;
    }, 650);
  }
}
